import React, { useRef, useCallback, useState } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, IconButton, Tooltip, Chip, Typography, Paper, Button, Stack,
  Popper, ClickAwayListener, List, ListItem, ListItemButton, ListItemText,
  CircularProgress, InputAdornment,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import productoService from '../../../services/productoService';
import articuloXListaPrecioService from '../../../services/articuloXListaPrecioService';
import inventarioInformes from '../../../services/inventarioInformes';
import { resolverPrecioDesdeLista, obtenerPrecioBaseProducto } from '../../../utils/precioListaPedido';
import { formatCurrency } from '../../../utils/formatters';
import { POSLinea, SuggestionItem } from './types';
import FacturaPOSStockPopover from './FacturaPOSStockPopover';

interface Props {
  lineas: POSLinea[];
  nroLista?: string;
  readOnly?: boolean;
  onLineaChange: (id: string, field: keyof POSLinea, value: string | number) => void;
  onLineaFilled: (id: string, data: Partial<POSLinea>) => void;
  onEliminarLinea: (id: string) => void;
  onDuplicarLinea: (id: string) => void;
  onAgregarLineaVacia: () => void;
}

const NUMERIC_COLS = ['cantidad', 'precioUnitario', 'descuento'];
const NAV_COLS = ['codigoArticulo', 'descripcionArticulo', 'cantidad', 'precioUnitario', 'descuento', 'notas'];

const StockBadge: React.FC<{ stock?: number | null; onClick?: (e: React.MouseEvent<HTMLElement>) => void }> = ({ stock, onClick }) => {
  const sx = { height: 18, fontSize: '0.65rem', cursor: onClick ? 'pointer' : 'default' };
  if (stock == null)
    return <Chip label="?" size="small" variant="outlined" onClick={onClick} sx={sx} />;
  if (stock <= 0)
    return <Chip label="Sin stock" size="small" color="error" onClick={onClick} sx={sx} />;
  if (stock < 10)
    return <Chip label={String(stock)} size="small" color="warning" onClick={onClick} sx={sx} />;
  return <Chip label={String(stock)} size="small" color="success" onClick={onClick} sx={sx} />;
};

// ── Helpers para resolución de precio/stock ──────────────────────────────────
async function resolverPrecioConLista(
  producto: any,
  nroLista?: string,
): Promise<number> {
  let precio = obtenerPrecioBaseProducto(producto);
  if (nroLista) {
    try {
      const reg = await articuloXListaPrecioService.getArticuloXListaPrecio(
        nroLista,
        producto.codigoArticulo,
      );
      const res = resolverPrecioDesdeLista(producto, reg);
      if (res.tipo !== 'sinPrecio') precio = res.precioLista;
    } catch {
      /* sin precio en lista, usa precio catalogo */
    }
  }
  return precio;
}

async function obtenerStock(codigoArticulo: string): Promise<number | null> {
  try {
    const res = await inventarioInformes.getStockPorDeposito({
      codigoArticulo,
      pageSize: 50,
    });
    const rows: any[] = (res?.data ?? []).filter(
      (r: any) => (r.codigoArticulo ?? '').trim() === codigoArticulo.trim(),
    );
    if (rows.length === 0) return null;
    return rows.reduce((acc: number, r: any) => acc + (Number(r.disponibleDeposito) || 0), 0);
  } catch {
    return null;
  }
}
// ─────────────────────────────────────────────────────────────────────────────

const FacturaPOSLineasGrid: React.FC<Props> = ({
  lineas,
  nroLista,
  readOnly = false,
  onLineaChange,
  onLineaFilled,
  onEliminarLinea,
  onDuplicarLinea,
  onAgregarLineaVacia,
}) => {
  const tableRef = useRef<HTMLTableElement>(null);

  // Estado para resolución de código
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [searchState, setSearchState] = useState<{
    id: string;
    results: SuggestionItem[];
    anchorEl: HTMLElement | null;
  } | null>(null);

  // Estado para popover de stock por depósito
  const [stockPopover, setStockPopover] = useState<{
    codigoArticulo: string;
    descripcion: string;
    anchorEl: HTMLElement | null;
  } | null>(null);

  const focusCell = useCallback((rowIdx: number, colName: string) => {
    const el = tableRef.current?.querySelector<HTMLInputElement>(
      `[data-row="${rowIdx}"][data-col="${colName}"]`,
    );
    el?.focus();
    el?.select();
  }, []);

  // ── Resolución de código al presionar Enter ──────────────────────────────
  const handleResolveCodigo = useCallback(
    async (id: string, codigo: string, inputEl: HTMLElement | null) => {
      if (!codigo.trim()) return;
      setResolvingId(id);
      setSearchState(null);

      const rowIdx = lineas.findIndex((l) => l.id === id);

      try {
        // Intento lookup exacto por código
        const producto = await productoService.getProducto(codigo.trim());
        const precio = await resolverPrecioConLista(producto, nroLista);
        const stock = await obtenerStock(producto.codigoArticulo);

        onLineaFilled(id, {
          codigoArticulo: producto.codigoArticulo,
          descripcionArticulo: producto.descripcion,
          precioUnitario: precio,
          um: (producto as any).um ?? (producto as any).unidadMedida ?? '',
          stockDisponible: stock,
        });

        // Posicionarse en cantidad
        setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
      } catch {
        // No encontrado → buscar por descripción
        try {
          const res = await productoService.getProductos({
            searchTerm: codigo.trim(),
            pageNumber: 1,
            pageSize: 8,
          });
          const items: any[] = res.items ?? [];
          const enriched: SuggestionItem[] = items.map((p) => ({
            codigoArticulo: p.codigoArticulo,
            descripcion: p.descripcion,
            precioUnitario: obtenerPrecioBaseProducto(p),
            um: p.um ?? p.unidadMedida ?? '',
            stockDisponible: null,
          }));
          if (enriched.length === 1) {
            // Un único resultado → seleccionar directo
            const precio = await resolverPrecioConLista(enriched[0], nroLista);
            const stock = await obtenerStock(enriched[0].codigoArticulo);
            onLineaFilled(id, {
              codigoArticulo: enriched[0].codigoArticulo,
              descripcionArticulo: enriched[0].descripcion,
              precioUnitario: precio,
              um: enriched[0].um,
              stockDisponible: stock,
            });
            setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
          } else if (enriched.length > 1) {
            setSearchState({ id, results: enriched, anchorEl: inputEl });
          }
          // si 0 resultados, no hacer nada (usuario verá campo vacío)
        } catch {
          /* sin resultados */
        }
      } finally {
        setResolvingId(null);
      }
    },
    [lineas, nroLista, onLineaFilled, focusCell],
  );

  const handleSearchSelect = useCallback(
    async (item: SuggestionItem) => {
      if (!searchState) return;
      const { id } = searchState;
      setSearchState(null);
      setResolvingId(id);
      const rowIdx = lineas.findIndex((l) => l.id === id);

      const precio = await resolverPrecioConLista(item, nroLista);
      const stock = await obtenerStock(item.codigoArticulo);
      onLineaFilled(id, {
        codigoArticulo: item.codigoArticulo,
        descripcionArticulo: item.descripcion,
        precioUnitario: precio,
        um: item.um,
        stockDisponible: stock,
      });
      setResolvingId(null);
      setTimeout(() => focusCell(rowIdx, 'cantidad'), 60);
    },
    [searchState, lineas, nroLista, onLineaFilled, focusCell],
  );

  // ── Navegación por teclado ───────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (
      e: React.KeyboardEvent<HTMLInputElement>,
      rowIdx: number,
      colName: string,
    ) => {
      if (e.key === 'Escape') {
        setSearchState(null);
        return;
      }
      if (readOnly) return;
      const colIdx = NAV_COLS.indexOf(colName);

      if (e.key === 'Tab' && !e.shiftKey) {
        e.preventDefault();
        setSearchState(null);
        const isLastCol = colIdx === NAV_COLS.length - 1;
        if (!isLastCol) {
          focusCell(rowIdx, NAV_COLS[colIdx + 1]);
        } else if (rowIdx < lineas.length - 1) {
          focusCell(rowIdx + 1, NAV_COLS[0]);
        } else {
          onAgregarLineaVacia();
        }
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (colName === 'codigoArticulo') {
          const inputEl = (e.target as HTMLInputElement).closest(
            '.MuiFormControl-root',
          ) as HTMLElement | null;
          const id = lineas[rowIdx]?.id;
          if (id) handleResolveCodigo(id, (e.target as HTMLInputElement).value, inputEl);
        } else {
          if (rowIdx < lineas.length - 1) {
            focusCell(rowIdx + 1, NAV_COLS[0]);
          } else {
            onAgregarLineaVacia();
          }
        }
      }
    },
    [lineas, focusCell, onAgregarLineaVacia, handleResolveCodigo, readOnly],
  );

  const handleChange = (id: string, field: keyof POSLinea, raw: string) => {
    if (NUMERIC_COLS.includes(field as string)) {
      const v = parseFloat(raw.replace(',', '.'));
      onLineaChange(id, field, isNaN(v) ? 0 : v);
    } else {
      onLineaChange(id, field, raw);
    }
  };

  const cellSx = { p: '3px 4px', border: 0, borderBottom: '1px solid', borderColor: 'divider' };
  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 1 } };
  const numInputProps = (rowIdx: number, col: string) => ({
    'data-row': rowIdx,
    'data-col': col,
    style: { textAlign: 'right' as const, padding: '4px 6px', fontSize: '0.82rem' },
    min: 0,
  });
  const textInputProps = (rowIdx: number, col: string) => ({
    'data-row': rowIdx,
    'data-col': col,
    style: { padding: '4px 6px', fontSize: '0.82rem' },
  });

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small" ref={tableRef} sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ width: 110, fontWeight: 600, fontSize: '0.78rem' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 600, fontSize: '0.78rem' }}>Descripción</TableCell>
              <TableCell sx={{ width: 50, fontWeight: 600, fontSize: '0.78rem' }}>U/M</TableCell>
              <TableCell sx={{ width: 78, fontWeight: 600, fontSize: '0.78rem' }}>Stock</TableCell>
              <TableCell sx={{ width: 80, fontWeight: 600, fontSize: '0.78rem' }}>Cant.</TableCell>
              <TableCell sx={{ width: 100, fontWeight: 600, fontSize: '0.78rem' }}>Precio</TableCell>
              <TableCell sx={{ width: 72, fontWeight: 600, fontSize: '0.78rem' }}>Desc%</TableCell>
              <TableCell sx={{ width: 140, fontWeight: 600, fontSize: '0.78rem' }}>Notas</TableCell>
              <TableCell sx={{ width: 100, fontWeight: 600, fontSize: '0.78rem', textAlign: 'right' }}>
                Subtotal
              </TableCell>
              <TableCell sx={{ width: 56 }} />
            </TableRow>
          </TableHead>
          <TableBody>
            {lineas.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={10}
                  align="center"
                  sx={{ py: 5, color: 'text.disabled', fontSize: '0.9rem' }}
                >
                  Buscá un artículo arriba para agregarlo a la factura
                </TableCell>
              </TableRow>
            )}
            {lineas.map((linea, rowIdx) => {
              const isResolving = resolvingId === linea.id;
              return (
                <TableRow key={linea.id} hover>
                  {/* ── Código (con resolución + spinner) ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      value={linea.codigoArticulo}
                      onChange={(e) => handleChange(linea.id, 'codigoArticulo', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'codigoArticulo')}
                      inputProps={textInputProps(rowIdx, 'codigoArticulo')}
                      InputProps={
                        isResolving
                          ? {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <CircularProgress size={12} />
                                </InputAdornment>
                              ),
                              readOnly,
                            }
                          : { readOnly }
                      }
                      disabled={isResolving}
                      sx={{ ...inputSx, '& input': { fontFamily: 'monospace' } }}
                    />
                  </TableCell>

                  {/* ── Descripción ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      value={linea.descripcionArticulo}
                      onChange={(e) =>
                        handleChange(linea.id, 'descripcionArticulo', e.target.value)
                      }
                      onKeyDown={(e) =>
                        handleKeyDown(e as any, rowIdx, 'descripcionArticulo')
                      }
                      inputProps={textInputProps(rowIdx, 'descripcionArticulo')}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle' }}>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {linea.um || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle' }}>
                    <StockBadge
                      stock={linea.stockDisponible}
                      onClick={
                        linea.codigoArticulo
                          ? (e) =>
                              setStockPopover({
                                codigoArticulo: linea.codigoArticulo,
                                descripcion: linea.descripcionArticulo,
                                anchorEl: e.currentTarget,
                              })
                          : undefined
                      }
                    />
                  </TableCell>

                  {/* ── Cantidad ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={linea.cantidad || ''}
                      onChange={(e) => handleChange(linea.id, 'cantidad', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'cantidad')}
                      inputProps={numInputProps(rowIdx, 'cantidad')}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  {/* ── Precio ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={linea.precioUnitario || ''}
                      onChange={(e) =>
                        handleChange(linea.id, 'precioUnitario', e.target.value)
                      }
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'precioUnitario')}
                      inputProps={numInputProps(rowIdx, 'precioUnitario')}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  {/* ── Descuento ── */}
                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      type="number"
                      value={linea.descuento || ''}
                      onChange={(e) => handleChange(linea.id, 'descuento', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'descuento')}
                      inputProps={{ ...numInputProps(rowIdx, 'descuento'), max: 100 }}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell sx={cellSx}>
                    <TextField
                      size="small"
                      fullWidth
                      multiline
                      maxRows={3}
                      value={linea.notas ?? ''}
                      onChange={(e) => handleChange(linea.id, 'notas', e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e as any, rowIdx, 'notas')}
                      inputProps={textInputProps(rowIdx, 'notas')}
                      InputProps={{ readOnly }}
                      sx={inputSx}
                    />
                  </TableCell>

                  <TableCell sx={{ ...cellSx, textAlign: 'right', verticalAlign: 'middle' }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      color={linea.subtotal > 0 ? 'text.primary' : 'text.disabled'}
                    >
                      {formatCurrency(linea.subtotal)}
                    </Typography>
                  </TableCell>

                  <TableCell sx={{ ...cellSx, verticalAlign: 'middle' }}>
                    <Stack direction="row" spacing={0}>
                      <Tooltip title="Duplicar línea">
                        <IconButton size="small" onClick={() => onDuplicarLinea(linea.id)} disabled={readOnly}>
                          <DuplicateIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => onEliminarLinea(linea.id)}
                          disabled={readOnly}
                        >
                          <DeleteIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ── Dropdown de búsqueda (Popper, fuera del TableContainer) ── */}
      {searchState && (
        <ClickAwayListener onClickAway={() => setSearchState(null)}>
          <Popper
            open
            anchorEl={searchState.anchorEl}
            placement="bottom-start"
            style={{ zIndex: 1400, minWidth: 380 }}
          >
            <Paper elevation={6} sx={{ borderRadius: 2, overflow: 'hidden', maxHeight: 300, overflowY: 'auto' }}>
              <List dense disablePadding>
                {searchState.results.map((s, idx) => (
                  <ListItem
                    key={s.codigoArticulo}
                    disablePadding
                    divider={idx < searchState.results.length - 1}
                  >
                    <ListItemButton
                      onClick={() => handleSearchSelect(s)}
                      sx={{ py: 0.75, px: 1.5 }}
                    >
                      <ListItemText
                        primary={
                          <Box display="flex" alignItems="center" gap={1}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 80, fontFamily: 'monospace', flexShrink: 0 }}
                            >
                              {s.codigoArticulo}
                            </Typography>
                            <Typography variant="body2" flex={1} noWrap>
                              {s.descripcion}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" color="primary.main" fontWeight={600}>
                            {formatCurrency(s.precioUnitario)}
                            {s.um ? ` / ${s.um}` : ''}
                          </Typography>
                        }
                        disableTypography
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Popper>
        </ClickAwayListener>
      )}

      <Box mt={1}>
        <Button size="small" startIcon={<AddIcon />} onClick={onAgregarLineaVacia} variant="text" disabled={readOnly}>
          Agregar línea vacía
        </Button>
      </Box>

      {/* ── Popover stock por depósito ── */}
      {stockPopover && (
        <FacturaPOSStockPopover
          codigoArticulo={stockPopover.codigoArticulo}
          descripcion={stockPopover.descripcion}
          anchorEl={stockPopover.anchorEl}
          onClose={() => setStockPopover(null)}
        />
      )}
    </Box>
  );
};

export default FacturaPOSLineasGrid;
